import { createRoot } from 'react-dom/client'
import { lazy, Suspense, type ComponentType } from 'react'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { isMarketingPath } from './marketing/routes'
import { hasOAuthHashSession } from './lib/oauthHashSession'
import './index.css'
import '@fontsource/inter/latin-400.css'
import '@fontsource/inter/latin-500.css'
import '@fontsource/inter/latin-600.css'
import '@fontsource/inter/latin-700.css'
import { registerDynamicImportRecovery } from './utils/dynamic-import-recovery'
import { bootstrapDemoMode } from './demo/config'

const StartApp = lazy(() => import('./marketing/StartApp'))
const ThemedEntry = lazy(() => import('./theme/ThemedEntry'))

function SiteEntry({ StartEntry }: { StartEntry: ComponentType }) {
  const { pathname } = useLocation()
  // OAuth callbacks must still reach the application's session bridge.
  const hasOAuthSession = hasOAuthHashSession()
  if (pathname.replace(/\/$/, '') === '/start' && !hasOAuthSession) {
    return <Suspense fallback={null}><StartEntry /></Suspense>
  }

  // A trailing splat owns every nested path, e.g. /clients/:id/details-v2/overview.
  const appRoute = __APP_ROUTE_PATTERNS__.some(route => {
    const pattern = route.replace(/:[^/]+/g, '[^/]+').replace(/\/\*$/, '(?:/.*)?')
    return new RegExp('^' + pattern + '$').test(pathname)
  })
  const marketing = (isMarketingPath(pathname) || !appRoute) && !hasOAuthSession
  return <Suspense fallback={null}><ThemedEntry marketing={marketing} /></Suspense>
}

registerDynamicImportRecovery()
bootstrapDemoMode()

async function mount() {
  // Keep the prerendered landing page visible while its small entry downloads.
  // Starting a suspended React root immediately would erase that useful HTML.
  const StartEntry = window.location.pathname.replace(/\/$/, '') === '/start' && !hasOAuthHashSession()
    ? (await import('./marketing/StartApp')).default
    : StartApp

  createRoot(document.getElementById('root')!).render(
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <SiteEntry StartEntry={StartEntry} />
    </BrowserRouter>
  )
}

void mount()
